import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const WS_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') + '/ws';

export function usePartnerNotifications() {
  const { user, isPartner, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const socketRef = useRef(null);

  // Fetch initial unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !isPartner) return;
    try {
      const res = await api.get('/partner/notifications?limit=1&unread_only=true');
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* silent */ }
  }, [isAuthenticated, isPartner]);

  // Connect WebSocket
  useEffect(() => {
    if (!isAuthenticated || !isPartner || !user?.id) return;

    fetchUnreadCount();

    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    socket.on('connect', () => {
      socket.emit('join_partner_room', { partner_id: user.id });
    });

    socket.on('new_notification', (notification) => {
      setUnreadCount(prev => prev + 1);
      setNotifications(prev => [notification, ...prev].slice(0, 20));
      toast.info(notification.title, { description: notification.message });
    });

    socketRef.current = socket;

    return () => {
      if (socket.connected) {
        socket.emit('leave_partner_room', { partner_id: user.id });
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, isPartner, user?.id, fetchUnreadCount]);

  const markAllRead = useCallback(async () => {
    try {
      await api.put('/partner/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  }, []);

  return { unreadCount, notifications, markAllRead, refetch: fetchUnreadCount };
}
